import { Component, ElementRef, inject, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { NotificationService, GroupedNotifications } from '../../../services/notification.service';
import { Subscription } from 'rxjs';
import { LayoutService } from '../../service/layout.service';
import { AppConfigurator } from '../app.configurator';
import { CreditsService } from '../../../services/credits.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { selectUserCredits } from '../../../store/selectors/credits.selectors';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    OverlayBadgeModule,
    PopoverModule,
    ButtonModule,
    ScrollPanelModule,
    TagModule,
    TooltipModule,
    AppConfigurator,
    OverlayBadgeModule
  ],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit, OnDestroy {
  layoutService = inject(LayoutService);
  private router = inject(Router);
  private store = inject(Store);
  private notificationService = inject(NotificationService);
  private creditsService = inject(CreditsService);
  private el = inject(ElementRef);
  userCredits2 = toSignal(this.store.select(selectUserCredits))
  private subscriptions: Subscription[] = [];

  unreadCount = computed(() => this.notificationService.unreadCount());
  notifications = computed(() => this.notificationService.notifications());
  isLoadingNotifications = signal<boolean>(false);
  hasOpenedNotifications = false;
  alreadyMarkedAllThisSession = false;
  notificationPopoverVisible = false;
  profilePopoverVisible = false;

  currentUser: any = null;
  onNotificationsOpened() {
    this.hasOpenedNotifications = true;
    this.alreadyMarkedAllThisSession = false;
  }
  onNotificationsClosed() {
    if (this.hasOpenedNotifications && !this.alreadyMarkedAllThisSession && this.unreadCount() > 0) {
      this.markAllAsRead();
      this.alreadyMarkedAllThisSession = true;
    }
    this.hasOpenedNotifications = false;
  }

  ngOnInit() {
    this.currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    this.loadInitialNotifications();
  }



  navigateTo(route: string) {
    this.router.navigate([route]);
  }
  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  iconType() {
    return 'pi pi-angle-left'
    //return this.layoutService.state.staticMenuDesktopInactive ? 'pi pi-bars' : 'pi pi-angle-left';
  }


  toggleDarkMode() {
    this.layoutService.toggleDarkMode();
  }

  toggleNotifications(popover: any, event: Event) {
    event.stopPropagation();
    this.notificationPopoverVisible = !this.notificationPopoverVisible;

    if (this.notificationPopoverVisible) {
      this.loadNotifications();
      popover.show(event);
    } else {
      popover.hide();
    }
  }

  toggleProfile(popover: any, event: Event) {
    event.stopPropagation();
    this.profilePopoverVisible = !this.profilePopoverVisible;

    if (this.profilePopoverVisible) {
      popover.show(event);
      this.profilePopoverVisible = false
    } else {
      popover.hide();
    }
  }

  private loadInitialNotifications() {
    if (this.currentUser.user_id) {

      const unreadSub = this.notificationService.getUnreadCount(this.currentUser.user_id).subscribe({
        next: (response) => this.notificationService.unreadCount.set(response.unread_count),
        error: (error) => console.error('Erreur chargement compteur:', error)
      });
      this.subscriptions.push(unreadSub);
    }
  }

  private loadNotifications() {
    if (!this.currentUser.user_id || this.isLoadingNotifications()) return;

    this.isLoadingNotifications.set(true);

    const notifSub = this.notificationService.getUserNotifications(this.currentUser.user_id).subscribe({
      next: () => this.isLoadingNotifications.set(false),
      error: (error) => {
        console.error('Erreur chargement notifications:', error);
        this.isLoadingNotifications.set(false);
      }
    });
    this.subscriptions.push(notifSub);
  }

  markNotificationAsRead(notification: any, event: Event) {
    event.stopPropagation();

    if (notification.is_read || !this.currentUser.user_id) return;

    const markSub = this.notificationService.markNotificationRead(
      this.currentUser.user_id,
      notification.id
    ).subscribe({
      error: (error) => console.error('Erreur marquage lecture:', error)
    });
    this.subscriptions.push(markSub);
  }

  markAllAsRead() {
    if (!this.currentUser.user_id) return;

    const markAllSub = this.notificationService.markAllNotificationsRead(this.currentUser.user_id).subscribe({
      error: (error) => console.error('Erreur marquage tout lu:', error)
    });
    this.subscriptions.push(markAllSub);
  }

  getNotificationIcon(notification: any): string {
    return notification.icon || 'pi-bell';
  }

  getNotificationIconClass(notification: any): string {
    const color = notification.icon_color || 'blue';
    return `bg-${color}-100 dark:bg-${color}-400/10 text-${color}-500`;
  }

  getNotificationsForGroup(groupName: keyof GroupedNotifications) {
    return this.notifications()[groupName] || [];
  }

  hasNotificationsInGroup(groupName: keyof GroupedNotifications): boolean {
    return this.getNotificationsForGroup(groupName).length > 0;
  }

  getGroupTitle(groupName: keyof GroupedNotifications): string {
    switch (groupName) {
      case 'today': return 'AUJOURD\'HUI';
      case 'yesterday': return 'HIER';
      case 'this_week': return 'CETTE SEMAINE';
      case 'older': return 'PLUS ANCIEN';
      default: return '';
    }
  }

  formatNotificationTime(dateString?: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) {
      return `il y a ${minutes} min`;
    } else if (hours < 24) {
      return `il y a ${hours}h`;
    } else if (days < 7) {
      return `il y a ${days}j`;
    } else {
      return date.toLocaleDateString('fr-FR');
    }
  }

  logout() {
    localStorage.removeItem('user');
    window.location.href = 'https://coteo-ia.com/';
  }

  goToAllNotifications() {
    this.router.navigate(['/notifications']);
  }

  refreshNotifications() {
    this.loadNotifications();
  }
}